import { Loading } from '@descomplica/foundation-ui-components';
import { observer, useViewModel } from '@descomplica/microfrontend-react';
import React, { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ExamAnswerDto } from '@/application/interfaces/exercises/pegasus.gateway';
import IconComponent from '@/presentation/components/icon/icon';

import Result from '../exam/components/result';
import { StartExamViewModel } from './start-exam.vm';

const Container = styled.div`
  width: 100%;
  font-family: 'Aprova Nova';
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  font-weight: normal;
  color: #111;
  background-color: #fff;
  position: relative;
  height: auto;
  min-height: 100vh;
  padding: 48px 52px;
  background: #333;

  @media (max-width: 860px) {
    padding: 20px 28px;
  }

  @media (max-width: 480px) {
    position: static;
    padding-inline: 16px;
  }
`;

const ConfirmationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const ExamTitle = styled.span`
  color: #fff;
  font-family: Aprova Nova;
  font-size: 48px;
  font-style: normal;
  font-weight: 800;
  line-height: 52px;
  letter-spacing: -1.8px;
`;

const HighlightContainer = styled.div`
  display: flex;
  padding: 16px 24px;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: 8px;
  background: #004d2f;
`;

const HighlightText = styled.div`
  color: #f2f2f3;
  font-family: Aprova Nova;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 26.4px */
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1 0 0;

  & > svg {
    margin-top: 3px;
  }
`;

const ExamInfoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const ExamInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ExamInfoTitle = styled.span`
  color: var(--color-primary-gray-05, #ddd);
  font-family: Aprova Nova;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%; /* 16px */
`;

const ExamInfoText = styled.span`
  color: var(--neutral-color-000, #fff);
  font-family: Aprova Nova;
  font-size: 64px;
  font-style: normal;
  font-weight: 700;
  line-height: 65%; /* 41.6px */
  letter-spacing: -3.2px;
`;

const ExamInfoDetail = styled.span`
  color: var(--neutral-color-100, #e5e6e7);
  font-family: Aprova Nova;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -1px;
`;

const ExamInfoTime = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
`;

const Button = styled.button<{ showButton?: boolean }>`
  background: #00e88f;
  padding: 20px 32px;
  color: var(--black, #000);
  text-align: center;
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: Aprova Nova;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 100%;
  border-radius: 300px;
  color: #111111;

  &:disabled {
    opacity: 0.5;
  }
`;

export const StartExam: React.FC = () => {
  const { guid } = useParams();
  const navigate = useNavigate();
  const vm = useViewModel(StartExamViewModel);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    const loadExercises = async () => {
      if (guid) await vm.handleScreenEnter(guid);
    };

    loadExercises();
  }, [guid]);

  useEffect(() => {
    const sendExercises = async () => {
      const answers = localStorage.getItem('answeredQuestions');

      if (answers && !answers === undefined) {
        const parsedAnswers: ExamAnswerDto = JSON.parse(answers);
        if (parsedAnswers.exercises_guid === guid) {
          await vm.postExamAnswerQuestion(parsedAnswers);
          localStorage.removeItem('answeredQuestions');
          location.reload();
        }
      }
    };

    sendExercises();
  }, []);

  useEffect(() => {
    if (vm.showHighlightCountdown && vm.countdown > 0) {
      intervalRef.current = window.setInterval(() => {
        vm.setCountdown(vm.countdown - 1);
      }, 1000);
    } else if (vm.countdown === 0) {
      clearInterval(intervalRef.current);
      vm.setButtonEnabled(true);
    }
    return () => clearInterval(intervalRef.current);
  }, [vm.showHighlightCountdown, vm.countdown]);

  const startExamHandler = async () => {
    await vm.setStartExam(guid);
    navigate(`/exam/${guid}`);
  };

  const onStartExam = () => {
    if (vm.isQuiz) {
      vm.setShowHighlight(true);
      vm.setShowHighlightCountdown(true);
    } else {
      startExamHandler();
    }
  };

  const onConfirmCountdown = async () => {
    if (vm.buttonEnabled) {
      await vm.setStartExam(guid);

      window.onbeforeunload = null;
      window.parent.postMessage({ type: 'confirmCountdown', guid }, '*');
    }
  };

  if (vm.isLoading) {
    return (
      <Container>
        <Loading />
      </Container>
    );
  }

  return !vm.showResult ? (
    <Container>
      <ConfirmationContainer>
        <ExamTitle>Avaliação</ExamTitle>
        {vm.showHighlight && (
          <HighlightContainer>
            <HighlightText>
              <IconComponent name="info" />
              Após começar a avaliação, caso você saia, feche ou atualize a janela todo seu
              progresso será perdido.
            </HighlightText>
          </HighlightContainer>
        )}
        {!vm.showHighlightCountdown && (
          <ExamInfoContainer>
            <ExamInfo>
              <ExamInfoTitle>Questões</ExamInfoTitle>
              <ExamInfoText>{vm.exercises?.exercisesQuestion.length || 0}</ExamInfoText>
            </ExamInfo>
            <ExamInfo>
              <ExamInfoTitle>Tempo total de prova</ExamInfoTitle>
              <ExamInfoTime>
                <ExamInfoText>{vm.exercises?.examExercisesConfig.interval_minutes}</ExamInfoText>
                <ExamInfoDetail> min</ExamInfoDetail>
              </ExamInfoTime>
            </ExamInfo>
          </ExamInfoContainer>
        )}
        {!vm.examExercises && !vm.showHighlightCountdown && (
          <Button type="button" onClick={onStartExam}>
            Começar avaliação
          </Button>
        )}
        {vm.showHighlightCountdown && (
          <Button type="button" disabled={!vm.buttonEnabled} onClick={() => onConfirmCountdown()}>
            {vm.buttonEnabled ? 'Confirmar' : `Confirmar em (${vm.countdown}s)`}
          </Button>
        )}
      </ConfirmationContainer>
    </Container>
  ) : (
    <Result exercises={vm.exercises} />
  );
};

window.onload = function () {
  window.localStorage.removeItem('countdownDuration');
  return '';
};

export default observer(StartExam);
